import { useState } from 'react'
import { createPortal } from 'react-dom'
import ProjectSelect from '@/components/ProjectSelect'
import { FolderIcon, XMarkIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import { fmt } from '@/constants/ui-locale'

interface MoveProjectDialogProps {
  /** The project the meeting is filed under now; never empty. */
  currentProjectKey: string
  /**
   * Whether there are minutes to regenerate.
   *
   * Without them there is nothing for a regenerate to replace, so the offer is
   * left out rather than shown doing nothing.
   */
  hasMinutes: boolean
  /** Whether a transcript exists for a regenerate to read. */
  hasTranscript: boolean
  /** Why the last attempt came to nothing, if it did. */
  error: string | null
  /** Whether a move is in flight; the dialog stays open and waits it out. */
  busy: boolean
  onCancel: () => void
  /**
   * Move the meeting, optionally asking for new minutes afterwards.
   *
   * @param projectKey  The project picked in this dialog.
   * @param regenerate  Whether to write the minutes again under it.
   */
  onMove: (projectKey: string, regenerate: boolean) => void
}

/**
 * Ask before moving a meeting from one project to another.
 *
 * A move is not a correction to a field. The project being left forgets what
 * it learned from the meeting and the new one learns it instead, Backlog
 * issues already filed stay where they were, and any minutes on screen were
 * written under the old project. None of that is visible from a picker, so it
 * is said here, before anything is saved.
 *
 * The regenerate is offered as its own button rather than a checkbox: it
 * replaces minutes the reader may have edited by hand, and that is a different
 * decision from the move itself.
 */
export default function MoveProjectDialog({
  currentProjectKey,
  hasMinutes,
  hasTranscript,
  error,
  busy,
  onCancel,
  onMove,
}: MoveProjectDialogProps) {
  const { t } = useUiStrings()
  const [projectKey, setProjectKey] = useState('')
  const picked = projectKey.trim()
  // Moving somewhere is the whole point, and moving to where it already is
  // would ask the old project to forget a meeting it still holds.
  const canMove = Boolean(picked) && picked !== currentProjectKey && !busy
  const offersRegenerate = hasMinutes && hasTranscript

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/30 px-3 py-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="move-project-dialog-title"
      data-testid="move-project-dialog"
    >
      <button
        type="button"
        aria-label={t.close}
        className="fixed inset-0 cursor-default"
        onClick={onCancel}
      />

      <div className="animate-scale-in relative w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5 shadow-2xl sm:p-6">
        <button
          type="button"
          aria-label={t.close}
          onClick={onCancel}
          className="absolute right-3 top-3 rounded-md p-1 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[var(--accent)] dark:bg-blue-500/15">
            <FolderIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2
              id="move-project-dialog-title"
              className="text-lg font-bold text-[var(--text-primary)]"
            >
              {t.meetingProjectMoveOpen}
            </h2>
            <p className="mt-1 text-xs text-[var(--text-secondary)]">
              {`${t.projectLabel}: ${currentProjectKey}`}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <ProjectSelect
            id="move-project-dialog-select"
            value={projectKey}
            onChange={setProjectKey}
            disabled={busy}
          />
        </div>

        {/* Said only once a destination exists: `{to}` is half the sentence,
            and a warning about moving nowhere reads as a bug. */}
        {picked && picked !== currentProjectKey && (
          <div className="mt-4 space-y-2 text-xs text-[var(--text-secondary)]">
            <p className="text-[var(--text-primary)]">
              {fmt(t.meetingProjectMoveConfirm, { from: currentProjectKey, to: picked })}
            </p>
            <p>{fmt(t.meetingProjectMoveTicketsNote, { from: currentProjectKey })}</p>
            {offersRegenerate && <p>{t.meetingProjectMoveRegenerateNote}</p>}
          </div>
        )}

        {error && (
          <p role="alert" className="mt-3 text-xs text-red-600">
            {error}
          </p>
        )}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]"
          >
            {t.cancel}
          </button>
          <button
            type="button"
            onClick={() => onMove(picked, false)}
            disabled={!canMove}
            className="btn-secondary rounded-md px-3 py-1.5 text-xs"
          >
            {/* "Move only" is only a distinction while there is a regenerate
                to be only-ing against. */}
            {offersRegenerate ? t.meetingProjectMoveOnly : t.meetingProjectMove}
          </button>
          {offersRegenerate && (
            <button
              type="button"
              onClick={() => onMove(picked, true)}
              disabled={!canMove}
              className="btn-primary rounded-md px-3 py-1.5 text-xs"
            >
              {t.meetingProjectMoveAndRegenerate}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
